import { ErrorIcon, SuccessIcon } from "@src/assets";
import { CashLogoWithChain, Spinner, TokenLogo } from "@src/components";
import { useSwapContext } from "@src/contexts";
import { useMemo } from "react";
import {
  HistoryEntry,
  HistoryEntryAction,
  HistoryEntryActionLabel,
  HistoryEntryStatus,
} from "@src/interfaces";

export const HistoryRow = ({ txData }: { txData: HistoryEntry }) => {
  const { allTokens, isMultichain, supportedChainsMap } = useSwapContext();

  const token = useMemo(() => {
    if (!txData?.tokenAddress) return undefined;

    const address = txData.tokenAddress.toLowerCase();

    const fullMatch = allTokens.find(
      (t) =>
        t.address.toLowerCase() === address && t.chainId === txData.chainId,
    );

    if (fullMatch) return fullMatch;

    return allTokens.find((t) => t.address.toLowerCase() === address);
  }, [allTokens, txData.tokenAddress, txData.chainId]);

  const isCashIncome =
    txData.action === HistoryEntryAction.sell ||
    txData.action === HistoryEntryAction.deposit;

  const isCashExpense =
    txData.action === HistoryEntryAction.buy ||
    txData.action === HistoryEntryAction.withdraw;

  const isAmountShown = txData.status === HistoryEntryStatus.success;

  const getTransactionIcon = () => {
    if (
      txData.action === HistoryEntryAction.deposit ||
      txData.action === HistoryEntryAction.withdraw
    ) {
      return (
        <CashLogoWithChain
          chain={
            isMultichain && txData?.chainId
              ? supportedChainsMap[txData.chainId]
              : undefined
          }
          size="large"
        />
      );
    } else if (txData.tokenAddress === "") {
      return txData.status === HistoryEntryStatus.pending ? (
        <Spinner className="w-8 h-8" />
      ) : (
        <ErrorIcon className="w-8 h-8" />
      );
    } else if (
      txData.action === HistoryEntryAction.buy ||
      txData.action === HistoryEntryAction.sell ||
      txData.action === HistoryEntryAction.external_transfer
    ) {
      return (
        token && (
          <TokenLogo
            token={token}
            tokenLogoClassName="w-8 h-8 min-w-8"
            unknownTokenLogoClassName="w-8 h-8 min-w-8 text-md"
            withChain={isMultichain ? !!txData.chainId : false}
            chain={
              isMultichain && txData?.chainId
                ? supportedChainsMap[txData.chainId]
                : undefined
            }
          />
        )
      );
    } else {
      return <div className="w-8 h-8 bg-gray-500 rounded-full" />;
    }
  };

  return (
    <div className="flex justify-between items-center h-[65px] px-2">
      <div className="flex items-center gap-3">
        <div>{getTransactionIcon()}</div>
        <div className="flex flex-col items-start">
          <p className="text-theme_color text-sm font-medium">
            {HistoryEntryActionLabel[txData.action]}{" "}
            {token?.tokenId === "USDC" ? "Cash" : token?.symbol}
          </p>
          <p className="text-text_primary text-opacity-60 text-xs font-medium">
            {new Date(txData.date).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
      </div>
      <div className="flex gap-4 items-center">
        {Number(txData.priceUsd ?? 0) > 0 ? (
          <p
            className={`text-xs font-bold text-text_primary ${
              isCashIncome && isAmountShown && "!text-success_color"
            }`}
          >
            {isCashIncome && isAmountShown && "+$"}
            {isCashExpense && isAmountShown && "-$"}
            {txData.priceUsd}
          </p>
        ) : (
          "-"
        )}
        {txData.status === HistoryEntryStatus.success && <SuccessIcon />}
        {txData.status === HistoryEntryStatus.error && <ErrorIcon />}
        {txData.status === HistoryEntryStatus.pending && (
          <Spinner className="w-[12px] h-[12px] overflow-hidden" />
        )}
      </div>
    </div>
  );
};
